import { api } from 'api';
import ButtonCommon from 'components/common/ButtonCommon';
import {
  EDIT_RECEIVER_COUNTRY_NAME,
  EDIT_RECEIVER_FISTNAME_NAME,
  EDIT_RECEIVER_GENDER_NAME,
  EDIT_RECEIVER_LASTNAME_NAME,
  EDIT_RECEIVER_MOBILE_NAME,
  EDIT_RECEIVER_PHONE_NAME,
  EDIT_RECEIVER_POSTAL_NAME,
  EDIT_RECEIVER_RELATIONSHIP_NAME,
  EDIT_RECEIVER_STATE_NAME,
  EDIT_RECEIVER_STREET_NAME,
  EDIT_RECEIVER_SUBURB_NAME,
} from 'constants';
import useAuth from 'hooks/useAuth';
import useLang from 'hooks/useLang';
import styled from 'styled-components';
import { stringUtils } from 'utils';

import BodySectionHeader from 'components/FormAddReceiver/components/BodySectionHeader';
import CustomBanksAccordion from 'components/FormEditReceiver/components/CustomBanksAccordion';
import CustomEwalletsAccordion from 'components/FormEditReceiver/components/CustomEwalletsAccordion';
import FormInputBankEwalletRadio from 'components/FormEditReceiver/components/FormInputBankEwalletRadio';
import { getPayloadOfResidentialAddress } from 'components/FormEditReceiver/func';
import {
  DESKTOP_MIN_WIDTH,
  EDIT_RECEIVER_SELECT_TYPE_OF_RECEIVER_NAME,
  THROTTLE_SUBMIT_SEC,
} from 'constants';
import withUpdateReceiverAccountDetailsInitial from 'features/receiver/HOCs/withUpdateReceiverAccountDetailsInitial';
import useUpdateReceiver from 'features/receiver/hooks/useUpdateReceiver';
import { checkMaintenance } from 'helpers/maintenance';
import useFormAddReceiverStore from 'hooks/form-add-receiver/useFormAddReceiverStore';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useCommonStore from 'hooks/useCommonStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useThrottle from 'hooks/useThrottle';
import { isEmpty } from 'lodash';
import { Fade } from 'react-bootstrap';

const AccountDetails = withUpdateReceiverAccountDetailsInitial((props) => {
  const {
    countryConfig,
    currentStep,
    setCurrentStep,
    handleOnToggleCancelPopup,
    information,
    setInformation,
    validation,
    setValidation,
    checkAllowSubmit,
    bankInformation,
    setBankInformation,
    bankValidation,
    setBankValidation,
    ewalletInformation,
    setEwalletInformation,
    ewalletValidation,
    setEwalletValidation,
    isBanksChecked,
    setBanksChecked,
    handleOnToggleFormAddNewBank,
    handleOnToggleFormAddNewEwallet,
    deliveryMethods,
  } = props || {};

  const { t } = useLang();
  const { token } = useAuth();
  const { editReceiverInformation, currentReceiver, setLoading } =
    useCommonStore();
  const { personalDetails, residentialAddress } = editReceiverInformation || {};
  const { throttle, isThrottleLocked, resetThrottle } =
    useThrottle(THROTTLE_SUBMIT_SEC);

  const { isDesktop } = useDetectDevice();

  const { currentBusinessReceiver, resetCurrentBusinessReceiver } =
    useFormAddTransactionStore();
  const { setAddReceiverAccountDetails } = useFormAddReceiverStore();

  const { onSaveUpdateReceiverInformation } = useUpdateReceiver();

  const isReceiverOfIndividualType =
    personalDetails[EDIT_RECEIVER_SELECT_TYPE_OF_RECEIVER_NAME] === 1;
  const isReceiverOfBusinessType =
    personalDetails[EDIT_RECEIVER_SELECT_TYPE_OF_RECEIVER_NAME] === 2;

  const renderAccountTypeReference = {
    [true]: (
      <CustomBanksAccordion
        banks={information?.banks}
        countryConfig={countryConfig}
        information={information}
        setInformation={setInformation}
        bankInformation={bankInformation}
        validation={validation}
        setBankInformation={setBankInformation}
        bankValidation={bankValidation}
        setBankValidation={setBankValidation}
        handleOnToggleFormAddNewBank={handleOnToggleFormAddNewBank}
      />
    ),
    [false]: (
      <CustomEwalletsAccordion
        ewallets={information?.ewallets}
        countryConfig={countryConfig}
        information={information}
        setInformation={setInformation}
        ewalletInformation={ewalletInformation}
        setEwalletInformation={setEwalletInformation}
        ewalletValidation={ewalletValidation}
        setEwalletValidation={setEwalletValidation}
        handleOnToggleFormAddNewEwallet={handleOnToggleFormAddNewEwallet}
      />
    ),
  };
  const renderDesc = () => {
    if (isReceiverOfIndividualType)
      return <Desc>{t('add_receiver_account_details_2')}</Desc>;

    if (isReceiverOfBusinessType)
      return <Desc>{t('add_receiver_account_details_3')}</Desc>;

    return null;
  };
  const renderFormInputBankEwalletRadio = () => {
    if (isReceiverOfIndividualType)
      return (
        <FormInputBankEwalletRadio
          deliveryMethods={deliveryMethods}
          isBanksChecked={isBanksChecked}
          setBanksChecked={setBanksChecked}
          banksLength={information?.banks?.length}
          ewalletsLength={information?.ewallets?.length}
        />
      );

    return null;
  };
  const renderAccountTypes = () => {
    if (deliveryMethods?.length)
      return (
        <>
          {renderDesc()}
          {renderFormInputBankEwalletRadio()}
          {renderAccountTypeReference[isBanksChecked]}
        </>
      );
    return (
      <Desc style={{ color: 'red' }}>
        {stringUtils.replaceKeyword(t('add_receiver_account_details_1'), [
          {
            key: 'countryName',
            value: personalDetails[EDIT_RECEIVER_COUNTRY_NAME]?.name,
          },
        ])}
      </Desc>
    );
  };

  const handleOnClickDeleteBankPopup = async () => {
    if (isEmpty(currentBusinessReceiver)) return;

    const { receiver, bank } = currentBusinessReceiver || {};

    if (!receiver?.receiverId || !bank?.id) return;

    try {
      await api.deleteBank(token, receiver?.receiverId, bank?.id);
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
      setLoading(false);
    }
  };
  const banksTransform = (banks) => {
    if (isEmpty(banks)) return [];
    return banks.map((bank) => ({
      ...bank,
      accountNumber: stringUtils.replaceAllCharacter(bank.accountNumber),
    }));
  };
  const handleSave = async () => {
    throttle(async () => {
      if (isReceiverOfBusinessType && !information?.banks?.length) {
        setValidation(true);
        return;
      }

      setLoading(true);

      const isAllowNext =
        Object.values(bankValidation).every((value) => !value) &&
        Object.values(ewalletValidation).every((value) => !value);

      if (isAllowNext) {
        setAddReceiverAccountDetails(information);

        try {
          const updateReceiverPayload = {
            // personalDetails
            firstname: personalDetails[EDIT_RECEIVER_FISTNAME_NAME],
            lastname: personalDetails[EDIT_RECEIVER_LASTNAME_NAME],
            gender: personalDetails[EDIT_RECEIVER_GENDER_NAME],
            mobile: personalDetails[EDIT_RECEIVER_MOBILE_NAME]
              ? {
                  id: currentReceiver?.mobile?.id || 0,
                  number: personalDetails[EDIT_RECEIVER_MOBILE_NAME],
                }
              : null,
            phone: personalDetails[EDIT_RECEIVER_PHONE_NAME]
              ? {
                  id: currentReceiver?.phone?.id || 0,
                  number: personalDetails[EDIT_RECEIVER_PHONE_NAME],
                }
              : null,
            relationship: personalDetails[EDIT_RECEIVER_RELATIONSHIP_NAME]?.key,
            // residentialAddress
            address: getPayloadOfResidentialAddress({
              addressId: currentReceiver?.address?.id,
              isManual:
                residentialAddress?.isEditReceiverEnterAddressManuallySelected,
              street: residentialAddress[EDIT_RECEIVER_STREET_NAME],
              suburb: residentialAddress[EDIT_RECEIVER_SUBURB_NAME],
              city: residentialAddress[EDIT_RECEIVER_STATE_NAME],
              postcode: residentialAddress[EDIT_RECEIVER_POSTAL_NAME],
              country: personalDetails[EDIT_RECEIVER_COUNTRY_NAME],
            }),
            // accountDetails
            banks: banksTransform(information?.banks),
            ewallets: information?.ewallets,
            // others
            favorited: false,
            email: null,
            dob: null,
          };
          console.log(
            `⭐ AccountDetails 👉 handleSave ➡️ onSaveUpdateReceiverInformation ➡️ updateReceiverPayload ↩`,
            updateReceiverPayload,
          );
          await onSaveUpdateReceiverInformation(updateReceiverPayload);

          setCurrentStep(currentStep + 1);
          resetCurrentBusinessReceiver();
          await handleOnClickDeleteBankPopup();
        } catch (error) {
          console.error(
            `🔥 AccountDetails ❌ handleSave ➡️ error 1 ↩`,
            error?.message,
          );
          checkMaintenance(error);
        } finally {
          resetThrottle();
          setLoading(false);
        }
      }
    });
  };
  const handleBackStep = () => {
    setAddReceiverAccountDetails(information);
    setCurrentStep(currentStep - 1);
  };
  const renderActionsButton = () => {
    if (isReceiverOfIndividualType)
      return (
        <Fade in appear>
          <NextWrap>
            <ButtonCommon
              value={t('button_back')}
              onClick={handleBackStep}
              styles={{
                margin: '0px',
                width: '50%',
              }}
              color="var(--ds-c-blue)"
              background="var(--ds-c-white)"
            />
            <ButtonCommon
              value={t('button_save')}
              color="var(--c-primary)"
              background="var(--bg-primary)"
              isFill={true}
              styles={{
                margin: '0px',
                width: '50%',
              }}
              onClick={handleSave}
              isUseKeyDown
              isDisabled={isThrottleLocked}
            />
          </NextWrap>
        </Fade>
      );

    if (isReceiverOfBusinessType)
      return (
        <Fade in appear>
          <NextWrap>
            <ButtonCommon
              value={t('button_back')}
              onClick={handleBackStep}
              styles={{
                margin: '0px',
                width: '50%',
              }}
              color="var(--ds-c-blue)"
              background="var(--ds-c-white)"
            />
            <ButtonCommon
              value={t('button_done')}
              color="var(--c-primary)"
              background="var(--bg-primary)"
              isFill={true}
              styles={{
                margin: '0px',
                width: '50%',
              }}
              onClick={handleSave}
              isUseKeyDown
              isDisabled={isThrottleLocked}
            />
          </NextWrap>
        </Fade>
      );

    return null;
  };

  return (
    <>
      <BodySectionHeader
        titleBodySection={!isDesktop && t('edit_receiver_title')}
        titleSection={t('label_account_details')}
        onToggleCancelPopup={handleOnToggleCancelPopup}
        isDisabled={false}
      />
      <AccountDetailsStyled>
        {renderAccountTypes()}
        {renderActionsButton()}
      </AccountDetailsStyled>
    </>
  );
});

const AccountDetailsStyled = styled.div`
  display: inline-block;
  width: 100%;
  margin-top: -8px;
  padding-inline: 0px;
  padding-bottom: 140px;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
    margin-top: 0px;
    padding-inline: 0px;
    padding-bottom: unset;
  }
`;

const Desc = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #0e1012;

  margin: 0;
  margin-bottom: 24px;
  padding: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const NextWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 77px;
  width: 100%;
  margin-inline: -16px;
  padding-inline: 16px;
  background: var(--ds-c-white);
  z-index: 1;
  position: fixed;
  bottom: 0;
  border-top: 1px solid #eef2f5;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;

    position: unset;
    margin-top: 40px;
    margin-bottom: 24px;
    margin-inline: unset;
    padding-inline: unset;
    width: 437px;
    height: 44px;
    border-top: unset;
  }
`;

export default AccountDetails;
